import React, { useContext, useMemo, useState } from "react";
import DetailLayout from "../components/DetailLayout";
import { Link, useHistory } from "react-router-dom";
import { Breadcrumb, Button, Checkbox, Spin } from "antd";
import CourseHeader from "../components/CourseHeader";
import style from "./index.module.scss";
import TopicReviewType from "./components/TopicReviewType";
import useRequest from "utils/useRequest";
import {
    BaseQuestionType,
    UrlParams,
    viewPaper,
    viewPaperListItem
} from "service/exercise";
import { CourseContext } from "../Store";
import CustomEmpty from "components/CourseComponents/CustomEmpty";
import _ from "underscore";
import { useAtomValue } from "jotai";
import {
    currentStudentAtom,
    exerciseIdAtom,
    joinedStudentsAtom,
    useUpdateMarkList
} from "service/stores/Exercise/Students";
import { useUnmount } from "react-use";

interface ExerciseReviewProps {
    match: { params: UrlParams };
}

export default function ExerciseReview({
    match: { params }
}: ExerciseReviewProps) {
    const history = useHistory();
    const { basename } = useContext(CourseContext);
    const { studentNo } = params;
    const [checked, setChecked] = useState(true);

    const exerciseId = useAtomValue(exerciseIdAtom);

    const [data, { loading }, refreshData] = useRequest(
        () => viewPaper(exerciseId, studentNo),
        {},
        [exerciseId, studentNo]
    );
    const students = useAtomValue(joinedStudentsAtom);

    const currentStu = useAtomValue(currentStudentAtom(studentNo));
    const currentIndex = students?.findIndex(
        (item: { studentNo: string; studentName: string }) =>
            item.studentNo === studentNo
    );

    const updateMarkList = useUpdateMarkList();

    // 注意, 不能随着批阅状态更新, 否则会造成列表不准的问题
    useUnmount(updateMarkList);

    //筛选出主观题(包括纯主观题和多题干题型中含有主观题)
    const currentData = useMemo(() => {
        if (checked) {
            const hasSubject = _.flatten(
                _.pluck(
                    data?.topics.filter(item => item.queTypeId > 5) || [],
                    "questions"
                )
            ).some(item => item.queTypeId === BaseQuestionType.Subjective);
            if (hasSubject) {
                return data?.topics.filter(
                    (item: viewPaperListItem) =>
                        item.queTypeId === BaseQuestionType.Subjective ||
                        item.queTypeId > 5
                );
            }
            return data?.topics.filter(
                (item: viewPaperListItem) =>
                    item.queTypeId === BaseQuestionType.Subjective
            );
        }
        return data?.topics;
    }, [checked, data]);

    function renderBreadcrumb() {
        return (
            <>
                <Breadcrumb.Item>
                    <Link to={`${basename}/exercise/students/${exerciseId}`}>
                        练习详情
                    </Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>提交批阅</Breadcrumb.Item>
            </>
        );
    }

    function goPrev() {
        const next = students[currentIndex - 1];
        if (!next) return;
        history.replace(next.studentNo);
        setChecked(true);
    }

    function goNext() {
        const next = students[currentIndex + 1];
        if (!next) return;
        history.replace(next.studentNo);
        setChecked(true);
    }

    function renderButtons() {
        return (
            <div>
                {currentIndex === 0 && students && students.length !== 1 && (
                    <span className={style.firstStu}>当前是第一位学生</span>
                )}
                {!_.isNull(students) &&
                    !_.isUndefined(currentIndex) &&
                    currentIndex !== 0 && (
                        <Button className={style.btnPrev} onClick={goPrev}>
                            上一份
                        </Button>
                    )}
                {!_.isNull(students) &&
                    !_.isUndefined(currentIndex) &&
                    currentIndex !== students?.length - 1 && (
                        <Button className={style.btnNext} onClick={goNext}>
                            下一份
                        </Button>
                    )}
                {students &&
                    currentIndex === students?.length - 1 &&
                    students.length !== 1 && (
                        <span className={style.lastStu}>
                            当前是最后一位学生
                        </span>
                    )}
            </div>
        );
    }

    return (
        <DetailLayout breadcrumb={renderBreadcrumb()}>
            {currentStu && (
                <CourseHeader
                    title={`${currentStu.name} ${currentStu.studentNo}`}
                    info={`提交时间 ${data?.stuSubmitTime}`}
                    extra={renderButtons()}
                />
            )}
            <Spin spinning={loading}>
                <div className={style.root}>
                    <div className={style.top}>
                        <div className={style.statics}>
                            <span>
                                共 {data?.topicCount} 题， {data?.totalScore} 分
                            </span>
                            <span>客观题得分：{data?.stuPaperObjScore}</span>
                            <span>主观题得分：{data?.stuPaperSubScore}</span>
                            <span className={style.red}>
                                总得分：{data?.stuPaperScore}
                            </span>
                        </div>

                        <Checkbox
                            checked={checked}
                            onChange={e => setChecked(e.target.checked)}>
                            仅显示主观题
                        </Checkbox>
                    </div>

                    <div className={style.topics}>
                        {currentData &&
                            currentData.length !== 0 &&
                            currentData.map((item: viewPaperListItem) => (
                                <TopicReviewType
                                    data={item}
                                    key={item.key}
                                    checked={checked}
                                    refreshData={refreshData}
                                    exerciseId={exerciseId}
                                    studentNo={studentNo}
                                />
                            ))}
                        {(!currentData || currentData.length === 0) && (
                            <CustomEmpty
                                style={{ padding: "100px 0" }}
                                description={"当前试卷中无主观题"}
                            />
                        )}
                    </div>
                </div>
            </Spin>
        </DetailLayout>
    );
}
